/// <amd-module name="Wit/DeliveryPlans/Board/Components/Board" />
// tslint:disable: member-ordering
import * as React from "react";
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { memoizeFunction } from "VSS/Core/Util/Memoize";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { localeDateFormat } from "VSS/Platform/Date";
import { css, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { IconSize } from "VSSUI/Icon";
import { IIntersectionContext, IntersectionContext } from "VSSUI/Intersection";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize, PillVariant } from "VSSUI/Pill";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { getColorString, parseColor } from "VSSUI/Utilities/Color";
import { Location } from "VSSUI/Utilities/Position";
import { Marker } from "Wit/Clients/Work/Contracts/Work";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";
import { CardConstants, DraggableCard } from "Wit/Common/Controls/Boards/Card";
import { calculateAbsolutePosition, DragMode, findElementAtPoint, IPosition, minDragDistance } from "Wit/Common/Controls/Boards/Draggable";
import {
    findElementClosestToHorizontalPoint,
    findElementClosestToVerticalPoint,
    IInsertionMarkerPosition,
    InsertionMarker
} from "Wit/Common/Controls/Boards/InsertionMarker";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { CoreFieldRefNames, OobFieldRefNames } from "Wit/Common/Generated/Constants";
import { Direction, IHotKeyEventData, IHotKeyEventService, IHotKeyEventType } from "Wit/Common/HotKeyEventService/IHotKeyEventService";
import { NewItemCallout } from "Wit/Common/WorkItemControls/NewItemCallout";
import { PlanData, WorkItemDependencyData, WorkItemDependencyPanelData } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import { WorkItemDependencyCallout } from "Wit/DeliveryPlans/Common/Components/WorkItemDependency";
import { ClientFields, FieldRefNames } from "Wit/DeliveryPlans/Common/Constants";
import { IDeliveryPlansTeamService } from "Wit/DeliveryPlans/Common/Services/DeliveryPlansTeamService";
import { IPlanService, PlanServiceEvents } from "Wit/DeliveryPlans/Common/Services/IPlanService";
import {
    addDaysToDate,
    compareDate,
    compareSpans,
    daysInMonth,
    daysInSpan,
    getSpan,
    getSpanFromMidpoint,
    isDateInSpan,
    isNextDay,
    ISpan,
    shiftSpan,
    today
} from "Wit/DeliveryPlans/Common/Utils/DateUtils";
import { getBacklogUrl, getSprintHubUrl } from "Wit/DeliveryPlans/Common/Utils/RoutingUtils";
import { TelemetryConstants } from "Wit/DeliveryPlans/Common/Utils/TelemetryUtils";
import { DependencyPanel } from "Wit/DeliveryPlans/DependencyPanel/DependencyPanel";
import { IDependencyService } from "Wit/DeliveryPlans/DependencyPanel/DependencyService";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { TypeIconSize, WorkItemTypeColorAndIcon, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import {
    CardDimensions,
    DependencyCardElement,
    ElementAndDependency,
    IDeliveryPlansWorkItem,
    IIteration,
    IObservableDeliveryPlansWorkItem,
    ITeam
} from "../../Common/Interfaces";
import { IDeliveryPlansWorkItemService, TopBottom } from "../../Common/Services/IDeliveryPlansWorkItemService";
import { binarySearch } from "../../Common/Utils/ArrayUtils";
import { BoardConstants } from "../BoardConstants";
import { BoardMarker } from "../BoardContracts";
import { DependencyGraphOverlay } from "../Graph/DependencyGraphOverlay";
import {
    BoardLayout,
    firstMarkerRow,
    IDeliveryPlansBoardLayoutService,
    monthHeaderRow,
    TeamLayout
} from "../Services/DeliveryPlansBoardLayoutService";
import {
    getDaysInViewport,
    getDayWidth,
    getPixelWidthFromDays,
    getScrollDistanceInDays,
    getSpacerWidth,
    getVisibleStartDate,
    isGridCalendarInitialized,
    scrollTimeline,
    scrollTimelineToDate
} from "../TimelineUtils";
import { StickyZone } from "./StickyZone";
interface IBoardProps {
    areAllDependenciesLoading: IObservableValue<boolean>;
    /**
     * Indicates if the user can edit the timeline view data, i.e. move cards, change dates, etc.
     */
    canEdit: boolean;

    /**
     * Indicates if cards are in a collapsed state
     */
    cardFieldsCollapsed: boolean;

    /**
     * Initial state of the expanded observable
     */
    expanded: boolean;

    /**
     * Timeline markers for important dates
     */
    markers: Marker[];

    /**
     * Function that will fetch data for the given span and perform corresponding updates
     */
    onFetchMoreData: (span: ISpan) => Promise<void>;

    /**
     * Handle when a card is moved to a new sprint
     */
    onItemMove: (
        item: IObservableDeliveryPlansWorkItem,
        iteration: IIteration,
        team: ITeam,
        previousItem: IDeliveryPlansWorkItem | undefined,
        nextItem: IDeliveryPlansWorkItem | undefined
    ) => void;

    /**
     * Handle when the user adds a new item.
     */
    onAddItem: (team: ITeam, iteration: IIteration, title: string, witType: string, direction: TopBottom) => void;

    /**
     * Handle when the user clicks to load more work items for a team
     */
    onLoadMoreWorkItems: (project: string, workItemIds: number[]) => Promise<void>;

    /**
     * Persist changes to the server
     */
    onSaveChanges: (itemId: number, changes: { [fieldName: string]: any }) => void;

    /**
     * Current scale of the timeline view. Higher scale means more zoomed in
     */
    scale: number;

    /**
     * Show empty field values on cards
     */
    showEmptyFields: boolean;

    /**
     * Show empty field values on cards
     */
    showChildRollup: boolean;

    /**
     * Mapping of tag name to color
     */
    tagStyles?: Map<string, string>;

    /**
     * Count of team timelines that will be rendered
     */
    teamCount: number;

    /**
     * Indicates if teams should be virtualized
     */
    virtualizeTeams: boolean;

    /**
     * Show all work items dependency lines
     */
    showAllDependencyLines: IObservableValue<boolean>;
}

namespace VirtualizationConstants {
    export const initialPageCount = 2;
    export const pageSize = 2;
}

interface IVirtualizationState {
    /**
     * The first team row that is currently rendered
     */
    firstMaterialized: number;
    /**
     * The last team row that is currently rendered
     */
    lastMaterialized: number;
    /**
     * Average height of grid rows
     */
    averageRowHeight: number;
    /**
     * Average height of team rows
     */
    averageTeamHeight: number;
}

interface IBoardState extends IVirtualizationState {
    cardFieldsCollapsed: boolean;
    pageSize: number;
    renderedTeamHeights: Map<number, number>;
    renderedTeams: Map<number, JSX.Element | null>;
    /**
     * the span that is rendered by Board
     */
    renderedSpan: ISpan;
    teamCount: number;
    scale: number;
    visibleMarkers: BoardMarker[];
}

interface TeamLayoutData {
    element: HTMLDivElement | null;
    endRow: number;
    startRow: number;
}

type ISelectedDependencyData = (WorkItemDependencyPanelData & { id: number }) | undefined;

/** Defined in a CSS variable that should never change */
const navBarHeight = 48;

/**
 * Draw the board.
 */
export class Board extends VssComponent<IBoardProps, IBoardState> {
    private _selectedDependencyData: ObservableArray<ISelectedDependencyData>;
    /**
     * True if any teams rows are expanded
     */
    private _expanded: IObservableValue<boolean>;
    /**
     * Ref to the div element that gets the grid layout
     */
    private _gridRef = React.createRef<HTMLDivElement>();
    /**
     * tracks the span for which data has been loaded
     */
    private _loadedSpan: ISpan;
    /**
     * tracks the current positioning of the viewport
     */
    private _visibleStartDate: Date | undefined;
    /**
     * State for users dragging the board background. This isn't in actual state because changes don't trigger renders.
     */
    private _dragState = {
        dragging: false,
        // current mouse position
        mouseLeft: 0,
        mouseTop: 0,
        // mouse position as of last update
        lastMouseLeft: 0,
        lastMouseTop: 0,
        // id of the requested animation frame, or 0 if there isn't one
        animationFrame: 0,
        // the element that is handling our vertical scrolling
        verticalScrollElement: null as HTMLElement | null
    };
    private _highlightedIterationElement: HTMLElement | undefined;
    private _insertionMarker = new ObservableValue<IInsertionMarkerPosition | undefined>(undefined);
    private _boardLayoutObservable: IObservableValue<BoardLayout | undefined>;
    private _spacerColumns: Date[] = [];

    private spacerElements: { [key: string]: HTMLElement } = {};
    private teamBackgroundElements: { [key: string]: TeamLayoutData | null } = {};
    private intersectionContext: IIntersectionContext;
    private scrollTop: number = 0;
    private _horizontalScrollSpeed = 300;

    private _timer = new TimerManagement();

    private _cardDimensions: IObservableValue<CardDimensions> = new ObservableValue({ height: 0, width: 0 });
    private _hotKeyEventService: IHotKeyEventService;

    public constructor(props: IBoardProps, context: IVssComponentContext) {
        super(props, context);

        const layoutService = this.context.pageContext.getService<IDeliveryPlansBoardLayoutService>("IDeliveryPlansBoardLayoutService");
        this._boardLayoutObservable = layoutService.getLayout();

        const teamCount = this.props.teamCount;
        const renderedSpan = getSpanFromMidpoint(today(), BoardConstants.defaultTimelineRenderedDays);
        this.state = {
            cardFieldsCollapsed: props.cardFieldsCollapsed,
            firstMaterialized: 0,
            lastMaterialized: !this.props.virtualizeTeams
                ? teamCount - 1
                : Math.min(VirtualizationConstants.initialPageCount * VirtualizationConstants.pageSize, teamCount - 1),
            pageSize: VirtualizationConstants.pageSize,
            renderedTeamHeights: new Map<number, number>(),
            renderedTeams: new Map<number, JSX.Element | null>(),
            renderedSpan,
            teamCount,
            averageRowHeight: 0,
            scale: props.scale,
            averageTeamHeight: 0,
            visibleMarkers: filterMarkers(this.props.markers, renderedSpan)
        };

        this._expanded = new ObservableValue(props.expanded);
        this._selectedDependencyData = new ObservableArray();
        this.props.showAllDependencyLines.subscribe(value => {
            if (value) {
                const workItemIds = Array.from(document.querySelectorAll<HTMLElement>(`.${CardConstants.cardClassName}`)).map(
                    getItemIdFromCardElement
                );
                this.showAllDependencyLines(workItemIds);
            } else {
                this._setSelectedDependencyData([]);
            }
        });

        this._hotKeyEventService = this.context.pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
    }

    public static getDerivedStateFromProps(props: Readonly<IBoardProps>, state: Readonly<IBoardState>): Partial<IBoardState> | null {
        if (props.cardFieldsCollapsed === state.cardFieldsCollapsed && props.scale === state.scale && props.teamCount === state.teamCount) {
            return null;
        }

        // if scale change results in different card display or we rendered a new span, clear row cache
        if (
            (state.scale > props.scale && props.scale === BoardConstants.compactScale) ||
            (state.scale < props.scale && props.scale === BoardConstants.compactScale + 1) ||
            (state.scale < props.scale && props.scale === BoardConstants.fullScale) ||
            (state.scale > props.scale && props.scale === BoardConstants.fullScale - 1) ||
            props.cardFieldsCollapsed !== state.cardFieldsCollapsed
        ) {
            // only clear caches of visible items - we don't care about fully re-rendering non-materialized items
            for (let index = state.firstMaterialized; index <= state.lastMaterialized; index++) {
                state.renderedTeams.delete(index);
                state.renderedTeamHeights.delete(index);
            }
        }

        // update materialized rows when count changes
        let firstMaterialized = state.firstMaterialized;
        let lastMaterialized = state.lastMaterialized;
        if (props.teamCount !== state.teamCount) {
            firstMaterialized = Math.max(0, Math.min(state.firstMaterialized, props.teamCount));
            if (props.virtualizeTeams) {
                // if all rows are already materialized in previous state, materialize an additional page size worth of rows
                const materializedCountIncreaseFactor = state.lastMaterialized === state.teamCount - 1 ? VirtualizationConstants.pageSize : 0;

                // lastMaterialized can't be greater than the total count
                const updatedLastMaterialized = Math.min(state.lastMaterialized + materializedCountIncreaseFactor, props.teamCount - 1);

                // lastMaterialized can't be less than firstMaterialized
                lastMaterialized = Math.max(firstMaterialized, updatedLastMaterialized);
            } else {
                lastMaterialized = props.teamCount - 1;
            }
        }

        return {
            cardFieldsCollapsed: props.cardFieldsCollapsed,
            firstMaterialized,
            lastMaterialized,
            scale: props.scale,
            teamCount: props.teamCount
        };
    }

    public render(): React.ReactNode {
        const { scale } = this.props;
        const { renderedSpan } = this.state;

        const layoutService = this.context.pageContext.getService<IDeliveryPlansBoardLayoutService>("IDeliveryPlansBoardLayoutService");
        const teams = layoutService.getTeams();

        return (
            <Observer teams={teams}>
                {({ teams }: { teams: ITeam[] }) => {
                    // memoize months and columns so we don't have to recalculate grid every time scale changes
                    // instead we'll just insert the current day pixel width and spacer width
                    const [dayElements, monthElements, gridTemplateColumns, spacerColumns] = getMonthsAndColumnsMemo(
                        renderedSpan,
                        "{day}",
                        "{spacer}",
                        teams
                    );
                    const hasColumns = dayElements.length > 0 && gridTemplateColumns;
                    this._spacerColumns = spacerColumns;

                    const dayPixels = getDayWidth(scale);
                    const dayPixelsStr = dayPixels + "px";
                    const spacerPixels = getSpacerWidth(scale) + "px";
                    // if the user's screen is wide enough, zooming from scale 1 -> 2 can cause issues because
                    // we update the scrollLeft before rendering the scaled-in timeline. This can result in gridRef.scrollLeft
                    // getting set to a value that is too large, which will in turn cause the timeline to be positioned incorrectly.
                    // To fix this, we render a single spacer column to be the same width as the timeline, guaranteeing that gridRef.scrollWidth will
                    // have enough space to let us always set the correct scrollLeft.
                    const gridSpacerColumnWidth = daysInSpan(this.state.renderedSpan) * dayPixels + "px";
                    const actualGridTemplateColumns =
                        `${BoardConstants.teamHeaderPixelWidth} ` +
                        gridTemplateColumns.split("{day}").join(dayPixelsStr).split("{spacer}").join(spacerPixels);

                    const endColumn = daysInSpan(this.state.renderedSpan) + this._spacerColumns.length;
                    const nonObservedGridItems = [
                        dayElements,
                        <StickyZone
                            key="sticky-zone"
                            threshold={navBarHeight}
                            observedElementPosition={{ gridRowStart: monthHeaderRow, gridColumnStart: 1, gridColumnEnd: endColumn }}
                        >
                            {monthElements}
                            {getScrollingButtonsMemo(this._onHorizontalScrollClick, endColumn)}
                        </StickyZone>,
                        <InsertionMarker key="insertion-marker" insertionMarkerPosition={this._insertionMarker} />
                    ];

                    return (
                        <Observer
                            boardLayout={{
                                filter: () => {
                                    // only clear caches of visible items - we don't care about fully re-rendering non-materialized items
                                    for (let index = this.state.firstMaterialized; index <= this.state.lastMaterialized; index++) {
                                        this.state.renderedTeams.delete(index);
                                        this.state.renderedTeamHeights.delete(index);
                                    }
                                    return true;
                                },
                                observableValue: this._boardLayoutObservable
                            }}
                        >
                            {({ boardLayout }: { boardLayout: BoardLayout | undefined }) => {
                                let teams: JSX.Element | undefined = undefined;
                                if (boardLayout?.teams) {
                                    teams = (
                                        <>
                                            <CollapseTeamsButton
                                                expanded={this._expanded}
                                                expandedCallback={expanded => {
                                                    this.expandedCallBack(expanded);
                                                }}
                                                key="collapse-teams-button"
                                            />
                                            {this._renderTeams(boardLayout)}
                                        </>
                                    );
                                }

                                // if we have 2 actual rows of content, applying grid-row: 1 / 2 to a div, it works like this
                                // ----------    <- 1
                                // div
                                // ----------    <- 2
                                // ----------    <- 3
                                // so need to add 1 to the highest row to get full span
                                const highestRow = (boardLayout?.highestRow || monthHeaderRow) + 1;
                                const fullVerticalSpanningItems = [
                                    <TimelineMarkersMemo
                                        key="timeline-markers"
                                        markers={this.state.visibleMarkers}
                                        scale={scale}
                                        maxRow={highestRow}
                                    />,
                                    <div className="team-background-header" key="team-background-header" style={{ gridRow: `1 / ${highestRow}` }} />,
                                    <div
                                        className="grid-spacer-column"
                                        key="grid-spacer-column"
                                        role="presentation"
                                        style={{ gridRow: `1 / ${highestRow}`, gridColumnStart: endColumn, width: gridSpacerColumnWidth }}
                                    />
                                ];

                                return (
                                    <IntersectionContext.Consumer>
                                        {intersectionContext => {
                                            this.intersectionContext = intersectionContext;
                                            return (
                                                <div
                                                    className="delivery-plans-board"
                                                    ref={this._gridRef}
                                                    style={{ gridTemplateColumns: actualGridTemplateColumns }}
                                                    onPointerDown={this._onPointerDown}
                                                >
                                                    <Observer selectedDependencyData={this._selectedDependencyData}>
                                                        {(props: { selectedDependencyData: ISelectedDependencyData[] }) => {
                                                            return (
                                                                props.selectedDependencyData?.length > 0 && (
                                                                    <DependencyGraphOverlay
                                                                        gridArea={`1 / 1 / ${highestRow} / ${endColumn}`}
                                                                        onClick={() => this._setSelectedDependencyData([])}
                                                                        onDependencyLineClick={this._onDependencyLineClick}
                                                                        parentOffsetLeft={this._gridRef.current?.offsetLeft ?? 0}
                                                                        parentOffsetTop={this._gridRef.current?.offsetTop ?? 0}
                                                                        data={this._getDependenciesAndCardElements(props.selectedDependencyData)}
                                                                        cardDimensions={this._cardDimensions}
                                                                    />
                                                                )
                                                            );
                                                        }}
                                                    </Observer>
                                                    {hasColumns && nonObservedGridItems}
                                                    {fullVerticalSpanningItems}
                                                    {teams}
                                                </div>
                                            );
                                        }}
                                    </IntersectionContext.Consumer>
                                );
                            }}
                        </Observer>
                    );
                }}
            </Observer>
        );
    }

    private expandedCallBack = (expanded: boolean) => {
        this._updateTeamExpandedHistoryState(BoardConstants.allTeamsKey, expanded);
        this._boardLayoutObservable.value?.teams.forEach(t => {
            t.expanded.value = expanded;
        });
    };

    private collapseAllTeamRowsCallback = () => {
        this._expanded.value = false;
        this.expandedCallBack(this._expanded.value);
    };

    private expandAllTeamRowsCallback = () => {
        this._expanded.value = true;
        this.expandedCallBack(this._expanded.value);
    };

    private panTimeLineCallback = ({ direction }: IHotKeyEventData) => {
        if (!direction) {
            return;
        }

        if (direction == Direction.Left) {
            this._onHorizontalScrollClick("left", this._horizontalScrollSpeed);
        }

        if (direction == Direction.Right) {
            this._onHorizontalScrollClick("right", this._horizontalScrollSpeed);
        }
    };

    private moveWorkItemCallback = (data: IHotKeyEventData) => {
        const itemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        const teamService = this.context.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");

        if (!data.workItemId || !data.teamId || !data.row || !data.direction) {
            return;
        }

        const item = itemService.get(data.workItemId);
        const team = teamService.getTeam(data.teamId);

        if (!item || !team) {
            return;
        }

        const iterationLayouts = this._boardLayoutObservable.value?.teams?.find(
            t => t.team.id === data.teamId && t.team.backlog === data.backlog
        )?.iterations;

        if (!iterationLayouts) {
            return;
        }

        const currentIterationLayout = iterationLayouts.find(it => it.iteration.id === data.iterationId);

        let iterationIndex = iterationLayouts.findIndex(x => x.iteration.id === data.iterationId);
        if (data.direction === Direction.Left && iterationIndex > 0) {
            iterationIndex--;
        } else if (data.direction === Direction.Right && iterationIndex < iterationLayouts.length - 1) {
            iterationIndex++;
        }

        const actualIterationLayout = iterationLayouts[iterationIndex];

        if (!currentIterationLayout) {
            return;
        }

        let previousItem: IDeliveryPlansWorkItem | undefined;
        let nextItem: IDeliveryPlansWorkItem | undefined;

        let futureNextRow = data.row;
        if (data.direction === Direction.Down) {
            futureNextRow = futureNextRow + 2;
        } else if (data.direction === Direction.Up) {
            futureNextRow--;
        }

        // 23 |	    	   |        Prev will be	23
        // 24 |	if go here | then   Next will be	24
        // ___________________________________________
        // 25 |	current    |
        // ___________________________________________
        // 26 |	if go here | then	Prev will be	26
        // 27 |			   |        Next will be	27
        // ___________________________________________
        //          futureNextRow is Next
        if (futureNextRow && actualIterationLayout == currentIterationLayout) {
            for (let i = 0; i < currentIterationLayout.workItems.length; i++) {
                const current = currentIterationLayout.workItems[i];
                if (current.startRow < futureNextRow && current.workItem.value.id !== data.workItemId) {
                    previousItem = current.workItem.value;
                }
                if (current.startRow >= futureNextRow && current.workItem.value.id !== data.workItemId) {
                    nextItem = current.workItem.value;
                    break;
                }
            }
        }

        this.props.onItemMove(item, actualIterationLayout.iteration, team, previousItem, nextItem);
    };

    private _hold = false;
    public async componentDidMount() {
        super.componentDidMount();

        this._hotKeyEventService.subscribe(this.collapseAllTeamRowsCallback, IHotKeyEventType.CollapseAllTeamRows);
        this._hotKeyEventService.subscribe(this.expandAllTeamRowsCallback, IHotKeyEventType.ExpandAllTeamRows);
        this._hotKeyEventService.subscribe(this.panTimeLineCallback, IHotKeyEventType.PanTimeline);
        this._hotKeyEventService.subscribe(this.moveWorkItemCallback, IHotKeyEventType.MoveWorkItem);

        // center timeline after initial render
        if (this._gridRef.current && isGridCalendarInitialized(this._gridRef.current) && !this._visibleStartDate) {
            // use the viewport width and the default starting date to determine what the first visible day should be
            const daysOnTimeline = getDaysInViewport(this._gridRef.current, this.props.scale);
            this._visibleStartDate = addDaysToDate(today(), daysOnTimeline / -2);
            scrollTimelineToDate(
                this._gridRef.current,
                this._visibleStartDate,
                this.state.renderedSpan.startDate,
                this.props.scale,
                this._spacerColumns
            );
        }

        const planService = this.context.pageContext.getService<IPlanService>("IPlanService");
        planService.subscribe(this._subscribeToPlanUpdates);

        await this._loadInitialTimelineData(today());
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(TelemetryConstants.Area, TelemetryConstants.PlanViewed);

        if (this.intersectionContext) {
            this.intersectionContext.register(this._onIntersect);
        }

        $DEBUG &&
            document.addEventListener("keypress", e => {
                if (e.key === "`") {
                    this._hold = !this._hold;
                    console.log(`hold: ${this._hold}`);
                }
            });
    }

    public componentDidUpdate(prevProps: IBoardProps) {
        if (prevProps.markers !== this.props.markers) {
            this.setState({ visibleMarkers: filterMarkers(this.props.markers, this.state.renderedSpan) });
        }
        const card = document.querySelector<HTMLElement>(`.${CardConstants.cardClassName}`);
        if (card) {
            if (this._cardDimensions.value.height != card.offsetHeight || this._cardDimensions.value.width != card.offsetWidth) {
                this._cardDimensions.value = { height: card.offsetHeight, width: card.offsetWidth };
            }
        }
    }

    public shiftTimelineBeforeScaleChange(newScale: number) {
        // position the span when the scale gets updated
        const currentScale = this.props.scale;
        if (this._gridRef.current && this._visibleStartDate && currentScale !== newScale && !this._hold) {
            // goal: keep the timeline centered on the same date when scaling in or out
            // what was the midpoint with the previous visible start date and scale?
            const previousDaysInViewport = getDaysInViewport(this._gridRef.current, currentScale);
            const midpoint = addDaysToDate(this._visibleStartDate, previousDaysInViewport / 2);
            const currentDaysInViewport = getDaysInViewport(this._gridRef.current, newScale);
            this._visibleStartDate = addDaysToDate(midpoint, -currentDaysInViewport / 2);

            this.updateTimelineData(this._gridRef.current, this._visibleStartDate, newScale);
            const newSpan = this.updateRenderedTimeline(this._gridRef.current, this._visibleStartDate, newScale);
            const renderedStartDate = newSpan?.startDate || this.state.renderedSpan.startDate;
            scrollTimelineToDate(this._gridRef.current, this._visibleStartDate, renderedStartDate, newScale, this._spacerColumns);
        }
    }

    public componentWillUnmount() {
        this._hotKeyEventService.unsubscribe(this.collapseAllTeamRowsCallback, IHotKeyEventType.CollapseAllTeamRows);
        this._hotKeyEventService.unsubscribe(this.expandAllTeamRowsCallback, IHotKeyEventType.ExpandAllTeamRows);
        this._hotKeyEventService.unsubscribe(this.panTimeLineCallback, IHotKeyEventType.PanTimeline);
        this._hotKeyEventService.unsubscribe(this.moveWorkItemCallback, IHotKeyEventType.MoveWorkItem);

        const planService = this.context.pageContext.getService<IPlanService>("IPlanService");
        planService.unsubscribe(this._subscribeToPlanUpdates);
        this._timer.dispose();

        if (this.intersectionContext) {
            this.intersectionContext.unregister(this._onIntersect);
        }
    }

    public getVirtualizationState(): IVirtualizationState {
        return {
            firstMaterialized: this.state.firstMaterialized,
            lastMaterialized: this.state.lastMaterialized,
            averageRowHeight: this.state.averageRowHeight,
            averageTeamHeight: this.state.averageTeamHeight
        };
    }

    private _renderTeams(boardLayout: BoardLayout) {
        const { firstMaterialized, lastMaterialized, teamCount } = this.state;
        const rows: Array<JSX.Element | null> = [];
        const endColumn = daysInSpan(this.state.renderedSpan) + this._spacerColumns.length;

        // Number of pages each spacer takes up. There are potentially two spacers above
        // or below the view port. They surround the focus range when the focus range is
        // not within the viewport.
        const topSpacerTeamCount = firstMaterialized;
        const bottomSpacerTeamCount = Math.max(0, teamCount - lastMaterialized - 1);

        if (topSpacerTeamCount > 0) {
            rows.push(this._renderSpacer("st2", boardLayout.teams.slice(0, topSpacerTeamCount), endColumn));
        }

        // Go through each of the rows and generate the child component.
        for (let rowIndex = 0; rowIndex < teamCount; rowIndex++) {
            const display = rowIndex >= firstMaterialized && rowIndex <= lastMaterialized;
            const teamLayout = boardLayout.teams[rowIndex];
            rows.push(this._renderTeamRow(boardLayout, teamLayout, rowIndex, endColumn, display));
        }

        if (bottomSpacerTeamCount > 0) {
            rows.push(
                this._renderSpacer(
                    "sb2",
                    boardLayout.teams.slice(boardLayout.teams.length - bottomSpacerTeamCount, boardLayout.teams.length),
                    endColumn
                )
            );
        }

        return rows;
    }

    private _renderTeamRow(boardLayout: BoardLayout, teamLayout: TeamLayout, index: number, endColumn: number, displayed: boolean) {
        const { canEdit, scale } = this.props;
        const { renderedSpan } = this.state;
        const team = teamLayout.team;
        const key = `${team.name}-${team.backlog}-row`;

        let teamRow = this.state.renderedTeams.get(index);
        const className = displayed ? undefined : "hidden";
        if (!teamRow) {
            teamRow = (
                <React.Fragment key={key}>
                    {index !== 0 && (
                        <div
                            key={`${team.name}-${team.backlog}-spacer`}
                            className={css("row-spacer", className)}
                            style={{ gridRow: teamLayout.startRow - 1 }}
                        />
                    )}
                    <TeamBackground
                        className={className}
                        teamIndex={index}
                        key={`${team.name}-${team.backlog}-bg`}
                        startRow={teamLayout.startRow}
                        endColumn={endColumn}
                        endRow={teamLayout.endRow}
                        ref={element => {
                            this.teamBackgroundElements[key] = { element, endRow: teamLayout.endRow, startRow: teamLayout.startRow };
                        }}
                        team={team}
                    />
                    <TeamHeader
                        className={className}
                        endRow={teamLayout.endRow}
                        expanded={teamLayout.expanded}
                        expandedCallback={expanded => {
                            // if any row is expanded, the board is considered to be expanded
                            this._expanded.value = boardLayout.teams.some(t => t.expanded.value);
                            this._updateTeamExpandedHistoryState(team.id + "-" + team.backlogName, expanded);
                        }}
                        key={`${team.name}-${team.backlog}-header`}
                        onLoadMoreWorkItems={this.props.onLoadMoreWorkItems}
                        pageContext={this.context.pageContext}
                        startRow={teamLayout.startRow}
                        teamLayout={teamLayout}
                    />
                    {team.iterations
                        .filter(it => compareSpans(renderedSpan, it) === 0)
                        .map(it => (
                            <Iteration
                                className={className}
                                key={`${team.name}-${team.backlogName}-${it.name}`}
                                iteration={it}
                                onAddItem={this.props.onAddItem}
                                pageContext={this.context.pageContext}
                                teamLayout={teamLayout}
                                {...getColumns(it, renderedSpan)}
                            />
                        ))}
                    <ConditionalChildren renderChildren={teamLayout.expanded} key={`${team.name}-${team.backlogName}-items`}>
                        {teamLayout.workItems
                            .filter(itemLayout => compareSpans(renderedSpan, itemLayout.workItem.value) === 0)
                            .map(itemLayout => (
                                <DraggableCard
                                    className={css(className, "focusable-board-item")}
                                    isMoveDisabled={() => !(canEdit && itemLayout.workItem.value.id > 0)}
                                    resizeDisabled={!(canEdit && itemLayout.workItem.value.id > 0)}
                                    key={"card-" + itemLayout.workItem.value.id}
                                    row={itemLayout.startRow}
                                    {...getColumns(itemLayout.workItem.value, renderedSpan)}
                                    itemLayout={itemLayout}
                                    cardFieldsCollapsed={this.props.cardFieldsCollapsed}
                                    compact={scale <= BoardConstants.compactScale}
                                    dataProps={{
                                        "data-backlog": team.backlog,
                                        "data-iteration": itemLayout.workItem.value.getIteration()?.id,
                                        "data-row": String(itemLayout.startRow),
                                        "data-team": team.id
                                    }}
                                    full={scale >= BoardConstants.fullScale}
                                    showEmptyFields={this.props.showEmptyFields}
                                    showChildRollup={this.props.showChildRollup}
                                    onCardClick={this._onShowDependencyLines}
                                    onDragComplete={this._onCardDrag}
                                    onDrag={this._onCardDrag}
                                    onRenderAdditionalContent={this._renderCardDependencyButton}
                                    onWorkItemClick={this._onWorkItemClick}
                                    rollupProviders={teamLayout.rollupProviders}
                                    tagStyles={this.props.tagStyles}
                                    disableEmptyFieldsToggle={true}
                                />
                            ))}
                    </ConditionalChildren>
                </React.Fragment>
            );

            this.state.renderedTeams.set(index, teamRow);
        }

        return teamRow;
    }

    private _renderCardDependencyButton = (observableItem: IObservableDeliveryPlansWorkItem) => {
        const item = observableItem.value;
        const hasDependencyError = item.getFieldValue<boolean>(ClientFields.HasDependencyError);
        const hasDependency = item.getFieldValue<boolean>(ClientFields.HasDependency);
        return (
            <Button
                className={css(
                    "dependency-button depth-8",
                    hasDependencyError && "violation",
                    !hasDependencyError && hasDependency && "non-violation"
                )}
                iconProps={{ iconName: "Link" }}
                onClick={e => {
                    if (e.defaultPrevented) {
                        return;
                    }

                    this._openDependencyPanel(item);
                    e.preventDefault();
                }}
            />
        );
    };

    private _openDependencyPanel = (workItem: IDeliveryPlansWorkItem) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return React.createElement(DependencyPanel, { onDismiss, workItem });
        });
    };

    private _renderSpacer(key: string, teams: TeamLayout[], endColumn: number): JSX.Element {
        const startRow = teams[0].startRow;
        const endRow = teams[teams.length - 1].endRow;
        let height = 0;
        for (const team of teams) {
            // use cached height if available, otherwise just estimate from row count
            height += this.state.renderedTeamHeights.get(team.index) || (team.endRow - team.startRow) * this.state.averageRowHeight;
        }

        return (
            <div
                aria-hidden="true"
                className="team-spacer invisible"
                key={key}
                ref={spacerElement => {
                    const existingElement = this.spacerElements[key];

                    if (spacerElement) {
                        if (existingElement !== spacerElement) {
                            if (existingElement) {
                                this.intersectionContext.unobserve(spacerElement);
                            }

                            this.intersectionContext.observe(spacerElement);
                            this.spacerElements[key] = spacerElement;
                        }
                    } else if (existingElement) {
                        this.intersectionContext.unobserve(existingElement);
                        delete this.spacerElements[key];
                    }
                }}
                role="presentation"
                style={{
                    height: height + "px",
                    gridRowStart: startRow,
                    gridRowEnd: endRow,
                    gridColumn: `1 / ${endColumn}`
                }}
            />
        );
    }

    private _setSelectedDependencyData(selectedDependencyData: ISelectedDependencyData[]) {
        this.state.renderedTeams.clear();
        if (selectedDependencyData?.length > 0 && this.props.virtualizeTeams) {
            // render all teams before rendering the graph overlay
            // this insures that we won't try to draw a line to a card that isn't rendered
            this.setState({ firstMaterialized: 0, lastMaterialized: this.state.teamCount - 1 }, () => {
                this._selectedDependencyData.value = selectedDependencyData;
            });
            this._hold = true;
        } else {
            queueMicrotask(() => {
                this._selectedDependencyData.value = selectedDependencyData;
            });

            this._hold = false;
        }

        if (!selectedDependencyData?.length) {
            if (this.props.showAllDependencyLines.value) {
                this.props.showAllDependencyLines.value = false;
            }
            queueMicrotask(() => {
                this._selectedDependencyData.removeAll();
            });
        }
    }

    private _onIntersect = (entries: IntersectionObserverEntry[]) => {
        const { firstMaterialized, lastMaterialized, renderedTeamHeights, teamCount } = this.state;
        let { averageRowHeight, averageTeamHeight } = this.state;

        // Ignore events if we dont have the grid element resolved (this should never happen).
        if (!this._gridRef.current || this._hold || !this.props.virtualizeTeams) {
            return;
        }

        // enumerate team backgrounds which will let us caculate team heights
        const rowElements = Object.values(this.teamBackgroundElements).slice(firstMaterialized, lastMaterialized + 1);

        // compute average team height
        if (averageTeamHeight === 0 || averageRowHeight === 0) {
            if (rowElements.length > 0) {
                let totalHeight = 0;
                let teamCount = 0;
                let rowCount = 0;

                // Loop through all children and average the rowHeights.
                for (let childIndex = 0; childIndex < rowElements.length; childIndex++) {
                    const rowData = rowElements[childIndex];
                    const rowElement = rowData?.element;
                    if (!rowData || !rowElement) {
                        continue;
                    }

                    const childHeight = rowElement.getBoundingClientRect().height;
                    if (childHeight > 0) {
                        totalHeight += childHeight;
                        teamCount++;
                        rowCount += rowData.endRow - rowData.startRow;
                    }
                }

                // Make sure we have at least one child row that has size.
                if (teamCount > 0) {
                    averageTeamHeight = totalHeight / teamCount;
                    averageRowHeight = totalHeight / rowCount;
                }
            }

            if (averageTeamHeight === 0) {
                return;
            }

            $DEBUG && console.log({ phase: "onIntersect - Compute RowHeight", averageTeamHeight, averageRowHeight });
            this.setState({ averageTeamHeight, averageRowHeight });
        }

        // return if there are no intersections to process and we already have spacers
        if (!entries.some(e => e.isIntersecting) && Object.values(this.spacerElements).length) {
            return;
        }

        // return if there wasn't an actual scroll
        const { scrollTop } = this.intersectionContext.root;
        if (scrollTop === this.scrollTop) {
            return;
        }

        // Determine the location of the intersection within the page. This is the element
        // we are scrolling within.
        const intersectionRect = this.intersectionContext.root.getBoundingClientRect();

        // Track the first and last row elements for adjusting the range.
        let firstMaterializedElement: Element | undefined;
        let lastMaterializedElement: Element | undefined;
        let firstMaterializedUpdated = firstMaterialized;
        let lastMaterializedUpdated = lastMaterialized;

        // Go through the viewport pages and determine if any are out of range and should be
        // paged out. Range is defined as more than 1 page of estimated rows away from the
        // nearest edge. If you dont allow for 1 page of estimated rows it may thrash pages
        // in and out of materialization.
        for (let childIndex = 0; childIndex < rowElements.length; childIndex++) {
            // Determine if this child is in the viewport, ignore rows that are not.
            const rowElement = rowElements[childIndex]?.element;
            if (!rowElement) {
                continue;
            }

            const rowIndex = getAttributeAsNumber(rowElement, "data-row-index");
            const rowRect = rowElement.getBoundingClientRect();

            if (rowIndex >= firstMaterialized && rowIndex <= lastMaterialized) {
                // Make sure to leave some extra room above and below the visible rectangle to handle
                // variable height rows. This helps prevent jittering when paging rows out.
                if (rowRect.bottom < intersectionRect.top - this.state.pageSize * averageTeamHeight) {
                    // save off the height so we can render an accurately sized spacer
                    renderedTeamHeights.set(firstMaterializedUpdated, rowRect.height + 8);
                    firstMaterializedUpdated++;
                } else if (rowRect.top > intersectionRect.bottom + this.state.pageSize * averageTeamHeight) {
                    // save off the height so we can render an accurately sized spacer
                    renderedTeamHeights.set(lastMaterializedUpdated, rowRect.height + 8);
                    lastMaterializedUpdated--;
                }
                // We will save the first and last rows for later computations.
                if (rowIndex === firstMaterialized) {
                    firstMaterializedElement = rowElement as Element;
                }

                if (rowIndex === lastMaterialized) {
                    lastMaterializedElement = rowElement as Element;
                }
            }
        }

        // If the row range is inverted (top above bottom) then all rows have been hidden and we should
        // recompute the viewport based on the scrollTop of our intersection and intersection height.
        if (firstMaterializedUpdated > lastMaterializedUpdated) {
            const offsetTop = scrollTop - (this._gridRef.current.offsetTop - this.intersectionContext.root.offsetTop);
            firstMaterializedUpdated = Math.max(0, Math.min(teamCount - 1, Math.floor(offsetTop / averageTeamHeight)) - this.state.pageSize);
            lastMaterializedUpdated = Math.min(
                teamCount - 1,
                firstMaterializedUpdated + Math.ceil(intersectionRect.height / averageTeamHeight + this.state.pageSize)
            );
        } else {
            // If the firstPage didn't move down, we may need more pages above.
            if (firstMaterializedUpdated === firstMaterialized && firstMaterializedElement) {
                const rowRect = firstMaterializedElement.getBoundingClientRect();
                const availableSpace = rowRect.top - intersectionRect.top;
                if (availableSpace > 0) {
                    firstMaterializedUpdated -= Math.ceil(availableSpace / averageTeamHeight);
                }
            }

            // If the lastPage didn't move up, we may need more pages below.
            if (lastMaterializedUpdated === lastMaterialized && lastMaterializedElement) {
                const rowRect = lastMaterializedElement.getBoundingClientRect();
                const availableSpace = intersectionRect.bottom - rowRect.bottom;
                if (availableSpace > 0) {
                    lastMaterializedUpdated += Math.ceil(availableSpace / averageTeamHeight);
                }
            }
        }

        // Make sure our page boundary stays in the available page range.
        firstMaterializedUpdated = Math.max(firstMaterializedUpdated, 0);
        lastMaterializedUpdated = Math.min(lastMaterializedUpdated, teamCount - 1);
        this.scrollTop = scrollTop;

        // Update our state if and only if something has changed.
        if (firstMaterializedUpdated !== firstMaterialized || lastMaterializedUpdated !== lastMaterialized) {
            // clear cached rows that are being paged in or out above the viewport
            const topStart = firstMaterializedUpdated < firstMaterialized ? firstMaterializedUpdated : firstMaterialized;
            const topEnd = firstMaterializedUpdated < firstMaterialized ? firstMaterialized : firstMaterializedUpdated;
            for (let index = topStart; index <= topEnd; index++) {
                this.state.renderedTeams.delete(index);
            }

            // clear cached rows that are being paged in or out below the viewport
            const bottomStart = lastMaterializedUpdated < lastMaterialized ? lastMaterializedUpdated : lastMaterialized;
            const bottomEnd = lastMaterializedUpdated < lastMaterialized ? lastMaterialized : lastMaterializedUpdated;
            for (let index = bottomStart; index <= bottomEnd; index++) {
                this.state.renderedTeams.delete(index);
            }

            this.setState({
                firstMaterialized: firstMaterializedUpdated,
                lastMaterialized: lastMaterializedUpdated
            });
        }
    };

    private _mergeSpan(newSpan: ISpan): ISpan {
        const currentSpan = this._loadedSpan;
        if (currentSpan) {
            if (currentSpan.endDate < newSpan.endDate) {
                return { startDate: currentSpan.startDate, endDate: newSpan.endDate };
            } else if (currentSpan.startDate > newSpan.startDate) {
                return { startDate: newSpan.startDate, endDate: currentSpan.endDate };
            } else {
                return currentSpan;
            }
        } else {
            return newSpan;
        }
    }

    private _getDependenciesAndCardElements(selectedDependencyData: ISelectedDependencyData[]): DependencyCardElement[] | undefined {
        if (!selectedDependencyData) {
            return;
        }

        const candidateDict: { [key: number]: HTMLElement } = {};
        Array.from(document.querySelectorAll<HTMLElement>(`.${CardConstants.cardClassName}`)).forEach(e => {
            const id = getItemIdFromCardElement(e);
            candidateDict[id] = e;
        });

        if (Object.keys(candidateDict).length === 0) {
            if (this.props.showAllDependencyLines.value) {
                this.props.showAllDependencyLines.value = false;
            }
            return;
        }

        const dependencies: DependencyCardElement[] = [];
        selectedDependencyData.forEach(dependencyData => {
            if (!dependencyData) {
                return;
            }
            const sourceElement = candidateDict[dependencyData.id];
            if (!sourceElement) {
                return;
            }

            // find all the cards we need to draw lines to
            const dependentItemCards = new Set<ElementAndDependency>();
            if (!dependencyData.dependencyData) {
                return;
            }
            dependencyData.dependencyData.forEach(dependency => {
                if (!dependency.isSourcePredecessor && dependencyData.id == dependency.source.id && this.props.showAllDependencyLines.value) {
                    return;
                }
                candidateDict[dependency.target.id] &&
                    dependentItemCards.add({
                        element: candidateDict[dependency.target.id],
                        dependency
                    });
            });

            dependencies.push({ workItemId: dependencyData.id, source: sourceElement, items: [...dependentItemCards] });
        });

        if (dependencies.every(dependency => !dependency.items.length)) {
            if (this.props.showAllDependencyLines.value) {
                this.props.showAllDependencyLines.value = false;
            }
            return;
        }
        return dependencies;
    }

    private _updateRenderedSpan(renderedSpan: ISpan, callback?: () => void) {
        // only clear visible team rows from the cache - we don't care about fully re-rendering non-materialized items
        for (let index = this.state.firstMaterialized; index <= this.state.lastMaterialized; index++) {
            this.state.renderedTeams.delete(index);
            this.state.renderedTeamHeights.delete(index);
        }

        this.setState(
            {
                renderedSpan,
                visibleMarkers: filterMarkers(this.props.markers, renderedSpan)
            },
            callback
        );
    }

    /** handles the user clicking the card title or the parent work item title */
    private _onWorkItemClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>, itemId: number) => {
        if (
            e.ctrlKey ||
            e.metaKey ||
            e.shiftKey ||
            ((e as React.MouseEvent<HTMLAnchorElement, MouseEvent>).button !== undefined &&
                (e as React.MouseEvent<HTMLAnchorElement, MouseEvent>).button !== 0) ||
            e.defaultPrevented
        ) {
            return;
        }

        const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        workItemService.openWorkItemDialog(itemId);
        e.preventDefault();
    };

    private _onShowDependencyLines = async (e: React.SyntheticEvent<HTMLElement>, workItemId: number) => {
        if (e.defaultPrevented) {
            return;
        }

        const dependencyService = this.context.pageContext.getService<IDependencyService>("IDependencyService");
        const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        const observableItem = workItemService.get(workItemId);
        if (!observableItem) {
            return;
        }

        observableItem.saving();
        const loadedDependencies = await dependencyService.getDependencies(workItemId);
        observableItem.saveComplete();
        if (loadedDependencies?.dependencyData) {
            this._setSelectedDependencyData([{ ...loadedDependencies, id: workItemId }]);
        }
    };

    private showAllDependencyLines = async (workItemIds: number[] | undefined) => {
        if (!workItemIds) {
            return;
        }
        const dependencyService = this.context.pageContext.getService<IDependencyService>("IDependencyService");
        this.props.areAllDependenciesLoading.value = true;
        const result = await dependencyService.getWorkItemsDependencies(workItemIds);
        const dependencyData: ISelectedDependencyData[] | undefined = result?.dependencies?.map(res => {
            return { dependencyData: res.dependencyData, id: res.workItemId } as ISelectedDependencyData;
        });

        if (dependencyData) {
            this._setSelectedDependencyData(dependencyData);
        } else {
            if (this.props.showAllDependencyLines.value) {
                this.props.showAllDependencyLines.value = false;
            }
        }
        this.props.areAllDependenciesLoading.value = false;
    };

    private _onDependencyLineClick = (e: React.MouseEvent<SVGPathElement, MouseEvent>, dependency: WorkItemDependencyData) => {
        e.preventDefault();
        e.stopPropagation();
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return React.createElement(WorkItemDependencyCallout, {
                onDismiss,
                anchorElement: e.currentTarget as any as HTMLElement,
                anchorOrigin: { horizontal: Location.center, vertical: Location.center },
                calloutOrigin: { horizontal: Location.center, vertical: Location.center },
                ...dependency
            });
        });
    };

    private _onHorizontalScrollClick = async (direction: "right" | "left", duration: number) => {
        const sign = direction === "left" ? -1 : 1;
        if (this._gridRef.current && this._visibleStartDate) {
            const daysToScroll = Math.min(getScrollDistanceInDays(this._gridRef.current, this.props.scale), BoardConstants.maxDaysToScroll);
            const daysInViewport = getDaysInViewport(this._gridRef.current, this.props.scale);
            const visibleStartDateBeforeScroll = this._visibleStartDate;
            const visibleEndDateBeforeScroll = addDaysToDate(visibleStartDateBeforeScroll, daysInViewport);
            this._visibleStartDate = addDaysToDate(this._visibleStartDate, sign * daysToScroll);

            if (daysInViewport > daysInSpan(this.state.renderedSpan)) {
                // if we're displaying more days than we can render
                // we can't scroll, we have to just move our renderedSpan
                this._updateRenderedSpan(shiftSpan(this.state.renderedSpan, sign * daysToScroll));
                return;
            }

            const scroll = async () => {
                let pixelsToScroll = getPixelWidthFromDays(daysToScroll, this.props.scale);
                await animateScroll((progress: number) => {
                    if (this._gridRef.current) {
                        const movement = pixelsToScroll * progress;
                        pixelsToScroll -= movement;
                        this._gridRef.current.scrollLeft += sign * movement;
                    }
                }, duration);
            };

            const daysFromEdge =
                direction === "left"
                    ? daysInSpan(this.state.renderedSpan.startDate, visibleStartDateBeforeScroll)
                    : daysInSpan(visibleEndDateBeforeScroll, this.state.renderedSpan.endDate);

            // re-render more aggressively so we can scroll before rendering as often as possible
            const distanceThreshold = 1 / 4;

            // does the timeline have enough days available to display the full timeline after the scroll?
            if (daysFromEdge > daysToScroll + BoardConstants.oneMonthInDays) {
                // yes -> scroll first then re-render to minimize stuttering
                await scroll();
                this.updateRenderedTimeline(this._gridRef.current, this._visibleStartDate, this.props.scale, distanceThreshold);
            } else {
                // no -> render first then scroll
                this.updateRenderedTimeline(this._gridRef.current, this._visibleStartDate, this.props.scale, distanceThreshold, scroll);
            }

            const spanToCheck = getSpan(this._visibleStartDate, daysInViewport);
            this.loadTimelineDataSpan(direction, spanToCheck);
        }
    };

    /**
     * Handles when a card is being dragged or resized, and when drag or resize completes.
     */
    private _onCardDrag = (e: PointerEvent, card: HTMLElement, mode: DragMode, position?: IPosition) => {
        if (mode === DragMode.move) {
            this._onCardMove(e, card);
        } else if (mode === DragMode.resizeLeft || mode === DragMode.resizeRight) {
            return this._onCardResize(e, card, mode, position);
        }
    };

    private _getIterationLayout = (teamId: string | undefined, backlog: string | undefined, iterationPath: string | undefined) =>
        this._boardLayoutObservable.value?.teams
            ?.find(t => t.team.id === teamId && t.team.backlog === backlog)
            ?.iterations.find(it => it.iteration.path === iterationPath);

    private _findIterationElement = (teamId: string | undefined, backlog: string | undefined, pageX: number, pageY: number) =>
        findElementAtPoint(`.iteration-background[data-team="${teamId}"][data-backlog="${backlog}"]`, pageX, pageY);

    private _findHorizontalClosestIterationElement = (teamId: string | undefined, backlog: string | undefined, pageX: number) =>
        findElementClosestToHorizontalPoint(document, `.iteration-background[data-team="${teamId}"][data-backlog="${backlog}"]`, pageX);

    private _getPositionInfo = (pageX: number, pageY: number) => {
        const teamService = this.context.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");
        const teamElement = findElementAtPoint(".team-background", pageX, pageY);
        const teamId = teamElement?.dataset.team;
        const backlog = teamElement?.dataset.backlog;
        const iterationElement = this._findIterationElement(teamId, backlog, pageX, pageY);
        const iterationPath = iterationElement?.dataset.iteration;

        return {
            teamId,
            team: teamId && teamService.getTeam(teamId),
            backlog,
            iterationLayout: this._getIterationLayout(teamId, backlog, iterationPath),
            iterationElement,
            iterationPath
        };
    };

    /**
     * Handles when the card is dragged while moving, and when the drag is completed.
     */
    private _onCardMove = (e: PointerEvent, card: HTMLElement) => {
        e.preventDefault();

        const cleanup = () => {
            this._highlightedIterationElement?.classList.remove("highlighted");
            this._highlightedIterationElement = undefined;
            this._insertionMarker.value = undefined;
        };
        const itemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        const teamService = this.context.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");
        const itemId = Number(card.dataset.itemid);
        const item = itemService.get(itemId);
        const { team, teamId, backlog, iterationElement, iterationLayout, iterationPath } = this._getPositionInfo(e.pageX, e.pageY);

        // find the card element that's closest to the mouse in the Y plane
        let closest: HTMLElement | undefined;
        let iterationRect: DOMRect | undefined;
        if (iterationElement && iterationLayout) {
            iterationRect = iterationElement.getBoundingClientRect();
            closest = findElementClosestToVerticalPoint(
                document,
                `.wit-card[data-team="${teamId}"][data-backlog="${backlog}"][data-iteration="${iterationLayout.iteration.id}"]`,
                e.pageY,
                item => {
                    // filter out items that aren't overlapping the iteration in the X plane
                    const itemRect = item.getBoundingClientRect();
                    return (
                        item.dataset.itemid !== card.dataset.itemid &&
                        ((itemRect.right >= iterationRect!.left && itemRect.left <= iterationRect!.left) ||
                            (itemRect.right >= iterationRect!.right && itemRect.left <= iterationRect!.right))
                    );
                }
            );
        }

        let insertionRow = closest && Number(closest.dataset.row);
        const closestRect = closest?.getBoundingClientRect();

        // if we're in the bottom half of the row, insert below the row
        const lowerHalf = closestRect && e.pageY > closestRect.top + window.scrollY + closestRect.height / 2;
        if (insertionRow && lowerHalf) {
            insertionRow++;
        }

        if (e.type === "pointermove") {
            // drag move
            if (!iterationRect || !item || !team || !iterationLayout || !teamService.teamOwnsWorkItem(team, item.value)) {
                cleanup();
                return;
            }

            // highlight the hovered iteration
            if (iterationElement !== this._highlightedIterationElement) {
                // doing this through React causes all the headers to re-render and takes too long
                this._highlightedIterationElement?.classList.remove("highlighted");
                iterationElement?.classList.add("highlighted");
                this._highlightedIterationElement = iterationElement;
            }

            if (!insertionRow) {
                this._insertionMarker.value = undefined;
                return;
            }

            if (closest && closestRect && iterationElement) {
                // the old way of doing this through react involved re-layout of the whole grid, which is too slow
                // so we absolutely position the insertion marker and hope the board doesn't change
                const updatedMarker = {
                    top: lowerHalf ? closestRect.bottom + 3 : closestRect.top - 5,
                    left: iterationRect.left,
                    width: iterationElement.offsetWidth
                };

                const oldInsertionMarker = this._insertionMarker.value;
                if (
                    !oldInsertionMarker ||
                    updatedMarker.top !== oldInsertionMarker.top ||
                    updatedMarker.left !== oldInsertionMarker.left ||
                    updatedMarker.width !== oldInsertionMarker.width
                ) {
                    this._insertionMarker.value = updatedMarker;
                }
            }
        } else if (e.type === "pointerup") {
            // drag complete
            cleanup();

            if (!iterationLayout) {
                return;
            }

            // find the items above and below the insertion point
            let previousItem: IDeliveryPlansWorkItem | undefined;
            let nextItem: IDeliveryPlansWorkItem | undefined;

            // no insertion row means the iteration has no items
            if (insertionRow) {
                for (let i = 0; i < iterationLayout.workItems.length; i++) {
                    const current = iterationLayout.workItems[i];
                    if (current.startRow < insertionRow && current.workItem.value.id !== itemId) {
                        previousItem = current.workItem.value;
                    }
                    if (current.startRow >= insertionRow && current.workItem.value.id !== itemId) {
                        nextItem = current.workItem.value;
                        break;
                    }
                }
            }

            if (item && iterationPath && team) {
                this.props.onItemMove(item, iterationLayout.iteration, team, previousItem, nextItem);
            }
        } else {
            throw Error(`Unhandled pointer event type ${e.type}`);
        }
    };

    /**
     * Handles the completion of a card resize
     */
    private _onCardResize(e: PointerEvent, card: HTMLElement, mode: DragMode, position?: IPosition) {
        const itemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        const itemId = Number(card.dataset.itemid);
        const item = itemService.get(itemId)?.value;
        const column =
            item &&
            getGridColumnFromMousePosition(item, mode, mode === DragMode.resizeLeft ? FieldRefNames.StartDate : FieldRefNames.TargetDate, e.pageX);
        const itemHasTargetDate = !!item?.getFieldValue<string>(OobFieldRefNames.TargetDate);

        if (e.type === "pointermove" && position) {
            // drag move
            // in order to snap to day boundries, we override the card position
            let positionToRender: IPosition | undefined = undefined;
            let calloutContent = "";
            if (column) {
                const formatter = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "numeric", day: "numeric" });
                if (mode === DragMode.resizeLeft) {
                    calloutContent = formatter.format(column.date);
                    positionToRender = {
                        ...position,
                        left: column.left,
                        width: position.width + (position.left - column.left)
                    };
                } else {
                    if (itemHasTargetDate) {
                        calloutContent = formatter.format(column.date);
                        positionToRender = { ...position, width: column.right - position.left };
                    } else {
                        const { teamId, backlog, iterationElement, iterationLayout } = this._getPositionInfo(e.pageX, e.pageY);
                        if (iterationElement && iterationLayout) {
                            calloutContent = iterationLayout.iteration.name;
                            positionToRender = { ...position, width: iterationElement.getBoundingClientRect().right - position.left };
                        } else {
                            const closestIterationElement = this._findHorizontalClosestIterationElement(teamId, backlog, e.pageX);
                            if (closestIterationElement) {
                                const closestIterationLayout = this._getIterationLayout(teamId, backlog, closestIterationElement.dataset.iteration);
                                calloutContent = closestIterationLayout?.iteration.name ?? "";
                                positionToRender = { ...position, width: closestIterationElement.getBoundingClientRect().right - position.left };
                            }
                        }
                    }
                }
            }
            return {
                overridePosition: positionToRender,
                overrideProps: { calloutContent }
            };
        } else if (e.type === "pointerup" && item && position) {
            // drag complete
            let column: ReturnType<typeof getGridColumnFromMousePosition>;
            let oldValue: Date;
            let field: FieldRefNames;
            if (mode === DragMode.resizeLeft || itemHasTargetDate) {
                if (mode === DragMode.resizeLeft) {
                    column = getGridColumnFromMousePosition(item, mode, FieldRefNames.StartDate, position.left);
                    oldValue = item.startDate;
                    field = FieldRefNames.StartDate;
                } else {
                    column = getGridColumnFromMousePosition(item, mode, FieldRefNames.TargetDate, position.left + position.width);
                    oldValue = item.endDate;
                    field = FieldRefNames.TargetDate;
                }
                if (column && compareDate(column.date, oldValue) !== 0) {
                    this.props.onSaveChanges(item.id, { [field]: column.date });
                }
            } else if (mode === DragMode.resizeRight) {
                const { teamId, backlog, iterationLayout } = this._getPositionInfo(e.pageX, e.pageY);
                const oldIterationLayout = this._getIterationLayout(teamId, backlog, item.iteration.path);
                const itemOldStartDate = item.getFieldValue<Date>(OobFieldRefNames.StartDate);
                const newStartDate = !itemOldStartDate ? oldIterationLayout?.iteration.startDate : itemOldStartDate;
                let newIterationPath: string | undefined;
                if (iterationLayout) {
                    newIterationPath = iterationLayout.iteration.path;
                } else {
                    const closestIterationElement = this._findHorizontalClosestIterationElement(teamId, backlog, e.pageX);
                    if (closestIterationElement) {
                        newIterationPath = closestIterationElement.dataset.iteration;
                    }
                }
                if (newStartDate && newIterationPath) {
                    this.props.onSaveChanges(item.id, {
                        [FieldRefNames.StartDate]: newStartDate,
                        [CoreFieldRefNames.IterationPath]: newIterationPath
                    });
                }
            }
        }
    }

    private _onDragScroll = () => {
        // check to see if we need to adjust the rendered timeline after scrolling
        if (this._gridRef.current && this._visibleStartDate && this.state.renderedSpan) {
            this._visibleStartDate = getVisibleStartDate(
                this._gridRef.current,
                this.state.renderedSpan.startDate,
                this.props.scale,
                this._spacerColumns
            );

            this.updateTimelineData(this._gridRef.current, this._visibleStartDate, this.props.scale);
            this.updateRenderedTimeline(this._gridRef.current, this._visibleStartDate, this.props.scale);
        }
    };

    private _debouncedOnDragScroll = this._timer.debounce(this._onDragScroll, 10);

    private _onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
        // if we're showing dependencies, we don't want dragging
        // because it may trigger a re-render which will offcenter the graph
        if (e.button !== 0 || e.defaultPrevented || this._selectedDependencyData?.length > 0) {
            return;
        }

        document.addEventListener("pointermove", this._onPointerMove);
        document.addEventListener("pointerup", this._onPointerUp);
        this._dragState.lastMouseLeft = e.pageX;
        this._dragState.lastMouseTop = e.pageY;
        // touch screen users expect to be able to scroll vertically by dragging, but this may be alarming to mouse users
        if (e.pointerType !== "mouse") {
            // find the first parent element with vertical scrolling
            let elem: HTMLElement | null;
            for (elem = e.currentTarget; !!elem; elem = elem.parentElement) {
                if (elem.offsetHeight < elem.scrollHeight) {
                    break;
                }
            }
            this._dragState.verticalScrollElement = elem;
        }
    };

    private _onPointerMove = (e: PointerEvent) => {
        const state = this._dragState;

        // wait until the pointer moves a minimum distance before starting the drag
        if (
            (!state.dragging &&
                Math.abs(e.pageX - state.lastMouseLeft) <= minDragDistance &&
                Math.abs(e.pageY - state.lastMouseTop) <= minDragDistance) ||
            e.defaultPrevented
        ) {
            return;
        }

        state.dragging = true;
        state.mouseLeft = e.pageX;
        state.mouseTop = e.pageY;
        if (!state.animationFrame) {
            state.animationFrame = requestAnimationFrame(() => {
                state.animationFrame = 0;
                if (!this._dragState.dragging) {
                    return;
                }

                if (this._gridRef.current) {
                    this._gridRef.current.scrollLeft += state.lastMouseLeft - state.mouseLeft;
                    if (state.verticalScrollElement) {
                        state.verticalScrollElement.scrollTop += state.lastMouseTop - state.mouseTop;
                    }
                }
                state.lastMouseLeft = state.mouseLeft;
                state.lastMouseTop = state.mouseTop;

                this._debouncedOnDragScroll();
            });
        }
    };

    private _onPointerUp = (e: PointerEvent) => {
        document.removeEventListener("pointermove", this._onPointerMove);
        document.removeEventListener("pointerup", this._onPointerUp);
        this._dragState.dragging = false;
    };

    private _subscribeToPlanUpdates = (planData: PlanData, action: string) => {
        if (action === PlanServiceEvents.planUpdate && this._gridRef.current && this._visibleStartDate) {
            // TODO: only reload if teams or field criteria changed
            // reload timeline data starting from current midpoint
            const layoutService = this.context.pageContext.getService<IDeliveryPlansBoardLayoutService>("IDeliveryPlansBoardLayoutService");
            layoutService.clear();

            const midpoint = addDaysToDate(this._visibleStartDate, getDaysInViewport(this._gridRef.current, this.props.scale) / 2);
            this._loadInitialTimelineData(midpoint);
        }
    };

    private _updateTeamExpandedHistoryState(teamName: string, expanded: boolean) {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = { [BoardConstants.teamKey + "/" + teamName]: expanded };
        settingsService.setEntries(entries, SettingsUserScope.Me);
        if (teamName != BoardConstants.allTeamsKey) {
            settingsService.removeEntries(BoardConstants.teamKey + "/" + BoardConstants.allTeamsKey, SettingsUserScope.Me);
        }
    }

    private async _loadInitialTimelineData(midpoint: Date) {
        const initialSpanToLoad = getSpanFromMidpoint(midpoint, BoardConstants.twoMonthsInDays);
        const leftSpan = {
            startDate: addDaysToDate(initialSpanToLoad.startDate, -2 * BoardConstants.twoMonthsInDays),
            endDate: initialSpanToLoad.startDate
        };
        const rightSpan = {
            startDate: initialSpanToLoad.endDate,
            endDate: addDaysToDate(initialSpanToLoad.endDate, 2 * BoardConstants.twoMonthsInDays)
        };

        this._loadedSpan = initialSpanToLoad;
        const initialSpanPromise = this.props.onFetchMoreData(initialSpanToLoad);

        // return as soon as any of the promises complete
        await Promise.race([initialSpanPromise, ...this.loadTimelineDataSpan("left", leftSpan), ...this.loadTimelineDataSpan("right", rightSpan)]);
    }

    /**
     * Check that a specific span is loaded and load it if it is not
     */
    private loadTimelineDataSpan(direction: "left" | "right", spanToLoad: ISpan): Promise<void>[] {
        const loadedSpan = this._loadedSpan;
        const spanToFetch =
            direction === "left"
                ? { startDate: spanToLoad.startDate, endDate: loadedSpan.startDate }
                : { startDate: loadedSpan.endDate, endDate: spanToLoad.endDate };

        const spans = [];
        let currentStartDate = spanToFetch.startDate;
        while (currentStartDate < spanToFetch.endDate) {
            const span = getSpan(currentStartDate, BoardConstants.twoMonthsInDays);
            spans.push(span);
            currentStartDate = span.endDate;
        }

        this._loadedSpan = this._mergeSpan(spanToFetch);
        return spans.map(span => this.props.onFetchMoreData(span));
    }

    /**
     * Check the bounds of the viewport and decide if more data is needed, i.e. we are approaching the bounds
     * of the loaded span
     */
    private updateTimelineData(gridRef: HTMLDivElement, visibleStartDate: Date, scale: number) {
        const loadedSpan = this._loadedSpan;
        const visibleDays = getDaysInViewport(gridRef, scale);
        const visibleSpan = getSpan(visibleStartDate, visibleDays);

        // if either edge of the visible span is within two months of where our data runs out, load a two month chunk
        const daysFromLeftEdge = daysInSpan(loadedSpan.startDate, visibleSpan.startDate);
        const daysFromRightEdge = daysInSpan(visibleSpan.endDate, loadedSpan.endDate);

        if (daysFromLeftEdge <= BoardConstants.twoMonthsInDays) {
            const leftSpanToLoad = { startDate: addDaysToDate(loadedSpan.startDate, -BoardConstants.twoMonthsInDays), endDate: loadedSpan.startDate };
            this._loadedSpan = this._mergeSpan(leftSpanToLoad);
            this.props.onFetchMoreData(leftSpanToLoad);
        }

        // both spans need to be checked, and we may load data for both
        if (daysFromRightEdge <= BoardConstants.twoMonthsInDays) {
            const rightSpanToLoad = { startDate: loadedSpan.endDate, endDate: addDaysToDate(loadedSpan.endDate, BoardConstants.twoMonthsInDays) };
            this._loadedSpan = this._mergeSpan(rightSpanToLoad);
            this.props.onFetchMoreData(rightSpanToLoad);
        }
    }

    /**
     * Update the renderedTimeline so that the visible portion of the timeline is roughly centered on it.
     * @param gridRef
     * @param visibleStartDate
     * @param distanceThreshold
     * @param callback
     * @returns the new rendered span
     */
    private updateRenderedTimeline(
        gridRef: HTMLDivElement,
        visibleStartDate: Date,
        scale: number,
        distanceThreshold = 1 / 8,
        callback?: () => void
    ): ISpan | undefined {
        const visibleDays = getDaysInViewport(gridRef, scale);
        const visibleSpan = getSpan(visibleStartDate, visibleDays);
        const daysToRender = Math.max(BoardConstants.defaultTimelineRenderedDays, Math.min(visibleDays * 2, BoardConstants.maxTimelineRenderedDays));
        if (visibleDays >= daysToRender || gridRef.scrollWidth <= gridRef.offsetWidth || this._hold) {
            // if we're displaying more days than we can render, we don't allow mouse scrolling
            // scrolling is accomplished with the scroll buttons updating the rendered span themselves.
            return;
        }
        // if either edge of the visible span is too close to the edge of the rendered span,
        // shift the rendered span in that direction
        const threshold = Math.floor((daysToRender - visibleDays) * distanceThreshold);
        const leftDistance = daysInSpan(this.state.renderedSpan.startDate, visibleSpan.startDate);
        const rightDistance = daysInSpan(visibleSpan.endDate, this.state.renderedSpan.endDate);
        const shiftLeft = this.state.renderedSpan.startDate >= visibleSpan.startDate || (leftDistance < threshold && leftDistance <= rightDistance);
        const shiftRight = this.state.renderedSpan.endDate <= visibleSpan.endDate || (rightDistance < threshold && rightDistance <= leftDistance);
        if (shiftLeft || shiftRight) {
            const newRenderedSpan = getSpanFromMidpoint(addDaysToDate(visibleStartDate, Math.floor(visibleDays / 2)), daysToRender);
            // pre-emptively position the span when renderedSpan.startDate gets updated (i.e. we rendered a different chunk of the timeline)
            // will the rendered portion of the timeline shift left or right?
            const sign = this.state.renderedSpan.startDate > newRenderedSpan.startDate ? 1 : -1;
            const daysAdded = daysInSpan(newRenderedSpan.startDate, this.state.renderedSpan.startDate);
            $DEBUG &&
                console.log(
                    `rendered span changing from ${spanToString(this.state.renderedSpan)} to ${spanToString(
                        newRenderedSpan
                    )}, adding ${daysAdded} days`
                );
            scrollTimeline(gridRef, sign * daysAdded, scale);
            this._updateRenderedSpan(newRenderedSpan, callback);
            return newRenderedSpan;
        }
    }
}

function spanToString(span: ISpan) {
    return `{${span.startDate.getFullYear()}-${span.startDate.getMonth() + 1}-${span.startDate.getDate()} - ${span.endDate.getFullYear()}-${
        span.endDate.getMonth() + 1
    }-${span.endDate.getDate()}}`;
}

function getItemIdFromCardElement(e: HTMLElement): number {
    return Number((e.closest("[data-itemid]") as HTMLElement)?.dataset.itemid);
}

function getAttributeAsNumber(element: Element, attributeName: string): number {
    const attributeValue = element.getAttribute(attributeName);
    if (attributeValue) {
        return parseInt(attributeValue, 10);
    }
    return -1;
}

function getGridColumnFromMousePosition(
    workItem: IDeliveryPlansWorkItem,
    dragMode: DragMode,
    whichDate: FieldRefNames.StartDate | FieldRefNames.TargetDate,
    pageX: number
) {
    const candidates = Array.from(document.querySelectorAll<HTMLElement>(".day, .day-spacer"));
    let target = binarySearch(candidates, el => {
        const rect = el.getBoundingClientRect();
        if (pageX < rect.left + window.scrollX) {
            return 1;
        }
        if (pageX > rect.right + window.scrollY) {
            return -1;
        }
        return 0;
    });

    // if user is hovering a spacer, just find the closest day and return that instead
    if (target?.classList.contains("day-spacer")) {
        target = getNextSibling(target, "day", dragMode === DragMode.resizeLeft ? "left" : "right");
    }

    const dateString = (target as HTMLElement)?.dataset?.date;
    if (!target || !dateString) {
        return undefined;
    }
    const position = calculateAbsolutePosition(target);
    const date = getDateFromString(dateString);
    if (
        (whichDate === FieldRefNames.StartDate && compareDate(date, workItem.endDate) >= 0) ||
        (whichDate === FieldRefNames.TargetDate && compareDate(workItem.startDate, date) >= 0)
    ) {
        return undefined;
    }

    return {
        date,
        left: position.left,
        right: position.left + target.offsetWidth
    };
}

/**
 * Returns a set of dates that should have a spacer column after them.
 * A date should have a spacer column if it is the end of an iteration and the next day is the start of an iteration.
 * This gives us some visual spacing between sprints.
 * @param teams
 */
function getSpacerColumns(teams: ITeam[] | undefined) {
    const spacerColumnSet = new Set<string>();
    const spacerColumnList: Date[] = [];
    if (!teams) {
        return { spacerColumnSet, spacerColumnList };
    }

    // if a date is in this set, we should add a spacer column after that date
    for (const team of teams) {
        let lastIteration: IIteration | undefined;
        for (const iteration of team.iterations) {
            if (lastIteration && isNextDay(lastIteration.endDate, iteration.startDate)) {
                // if two iterations are immediately adjacent, we add a spacer column to make it look nicer
                spacerColumnSet.add(getDateString(lastIteration.endDate));
                spacerColumnList.push(lastIteration.endDate);
            }
            lastIteration = iteration;
        }
    }

    return { spacerColumnSet, spacerColumnList };
}

/**
 * Return the grid columns for the given start and end dates.
 * @param o
 * @param monthMap
 */
function getColumns(o: ISpan, renderedSpan: ISpan) {
    // const startDate = isDateInSpan(o.startDate, visibleSpan) ? o.startDate : visibleSpan.startDate;
    // const endDate = isDateInSpan(o.endDate, visibleSpan) ? o.endDate : visibleSpan.endDate;
    let startDate: Date;
    let endDate: Date;
    if (o.startDate < renderedSpan.startDate) {
        startDate = renderedSpan.startDate;
    } else if (o.startDate <= renderedSpan.endDate) {
        startDate = o.startDate;
    } else {
        // TODO: we should probably not render this at all
        startDate = renderedSpan.endDate;
    }
    if (o.endDate > renderedSpan.endDate) {
        endDate = renderedSpan.endDate;
    } else if (o.endDate >= renderedSpan.startDate) {
        endDate = o.endDate;
    } else {
        // TODO: we should probably not render this at all
        endDate = renderedSpan.startDate;
    }

    const startColumn = `c${getDateString(startDate)}-start`;
    const endColumn = `c${getDateString(endDate)}-end`;
    return { startColumn, endColumn };
}

const monthFormatter = new Intl.DateTimeFormat(navigator.language, { month: "long" });
const monthAndYearFormatter = new Intl.DateTimeFormat(navigator.language, { month: "long", year: "numeric" });

/**
 * Calculates several time-related things for Board:
 * dayElements: invisible elements to render for locating date columns
 * monthHeaders: month header elements to render on the Board
 * gridTemplateColumns: value for the gridTemplateColumns CSS property for Board
 * @param start
 * @param end
 * @param dayWidth
 */
function getMonthsAndColumns(
    span: ISpan,
    dayWidth: string,
    spacerWidth: string,
    teams: ITeam[] | undefined
): [JSX.Element[], JSX.Element[], string, Date[]] {
    const { startDate: start, endDate: end } = span;
    const dayElements: JSX.Element[] = [];
    const monthElements: JSX.Element[] = [];
    const { spacerColumnSet, spacerColumnList } = getSpacerColumns(teams);
    const currentYear = new Date().getFullYear();

    let nextLineName: string | undefined;
    let gridTemplateColumns = "";
    for (
        let year = start.getFullYear(), month = start.getMonth();
        year < end.getFullYear() || (year === end.getFullYear() && month <= end.getMonth());
        month < 11 ? month++ : ((month = 0), year++)
    ) {
        const date = new Date(year, month, 1);
        const days = daysInMonth(date);
        const formatter = currentYear === year ? monthFormatter : monthAndYearFormatter;

        // rendered timeline may not include the entire first or last months
        let firstDay = 1;
        let lastDay = days;
        if (year === start.getFullYear() && month === start.getMonth()) {
            firstDay = start.getDate();
        } else if (year === end.getFullYear() && month === end.getMonth()) {
            lastDay = end.getDate();
        }

        const columnStart = `c${getDateString(year, month + 1, firstDay)}-start`;
        const columnEnd = `c${getDateString(year, month + 1, lastDay)}-end`;
        monthElements.push(
            <div
                key={"month-" + (year * 100 + month)}
                className="month-header padding-horizontal-8 text-ellipsis"
                style={{
                    gridRowStart: monthHeaderRow,
                    gridColumnStart: columnStart,
                    gridColumnEnd: columnEnd
                }}
            >
                {formatter.format(date)}
            </div>
        );

        for (let d = firstDay; d <= lastDay; d++) {
            const dateString = getDateString(year, month + 1, d);
            gridTemplateColumns += ` [c${dateString}-start ${nextLineName ? nextLineName : ""}] ${dayWidth}`;
            nextLineName = `c${dateString}-end`;
            dayElements.push(
                <div
                    key={"day-" + dateString}
                    className="day"
                    data-date={dateString}
                    style={{ gridRowStart: 1, gridColumnStart: `c${dateString}-start` }}
                />
            );
            if (spacerColumnSet.has(dateString)) {
                gridTemplateColumns += `[${nextLineName}] ${spacerWidth}`;

                // need to give spacer an element for card resizing
                dayElements.push(
                    <div
                        key={`day-${dateString}-spacer`}
                        className="day-spacer"
                        data-date={dateString}
                        style={{ gridRowStart: 1, gridColumnStart: nextLineName }}
                    />
                );
                nextLineName = undefined;
            }
        }
    }

    if (nextLineName) {
        gridTemplateColumns += `[${nextLineName}]`;
    }

    return [dayElements, monthElements, gridTemplateColumns, spacerColumnList];
}

const getMonthsAndColumnsMemo = memoizeFunction(getMonthsAndColumns);

const addButtonIconProps = { iconName: "Add", size: IconSize.small };
const dateFormatter = new Intl.DateTimeFormat(undefined, { month: "numeric", day: "numeric" });

function Iteration({
    className,
    startColumn,
    endColumn,
    pageContext,
    teamLayout,
    iteration,
    onAddItem
}: {
    className?: string;
    startColumn: string | number;
    endColumn: string | number;
    pageContext: IVssPageContext;
    teamLayout: TeamLayout;
    iteration: IIteration;
    onAddItem: (team: ITeam, iteration: IIteration, title: string, witType: string, direction: TopBottom) => void;
}): JSX.Element {
    const team = teamLayout.team;
    return (
        <>
            <IterationBackground
                className={className}
                endColumn={endColumn}
                endRow={teamLayout.endRow}
                iteration={iteration}
                startColumn={startColumn}
                startRow={teamLayout.startRow}
                team={team}
            />
            <IterationHeader
                className={className}
                endColumn={endColumn}
                iteration={iteration}
                onAddItem={onAddItem}
                pageContext={pageContext}
                row={teamLayout.startRow}
                startColumn={startColumn}
                teamLayout={teamLayout}
            />
        </>
    );
}

const deliveryPlansNewItemStorageKey = "{0}-{1}-new-item-callout-witType";

/** renders the sprint column header */
function IterationHeader({
    className,
    startColumn,
    endColumn,
    pageContext,
    row,
    teamLayout,
    iteration,
    onAddItem
}: {
    className?: string;
    startColumn: string | number;
    endColumn: string | number;
    pageContext: IVssPageContext;
    row: number;
    teamLayout: TeamLayout;
    iteration: IIteration;
    onAddItem: (team: ITeam, iteration: IIteration, title: string, witType: string, direction: TopBottom) => void;
}) {
    const [showNewItemCallout, setShowNewItemCallout] = React.useState<HTMLElement | undefined>();
    const onAddClickCb = (e: React.MouseEvent<HTMLElement>) => {
        const currentTarget = e.currentTarget;
        setShowNewItemCallout(old => {
            if (old) {
                return undefined;
            } else {
                return currentTarget;
            }
        });
    };

    const hotKeyEventService = pageContext.getService<IHotKeyEventService>("IHotKeyEventService");
    const backlogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
    const team = teamLayout.team;

    const getHeaderClass = (teamId: string, backlog: string, iterationId: string): string => {
        return `.iteration-header[data-team="${teamId}"][data-backlog="${backlog}"][data-iteration="${iterationId}"]`;
    };

    const newWorkItemCallback = ({ teamId, backlog, iterationId }: IHotKeyEventData) => {
        if (!iteration || !team) {
            return;
        }

        if (!teamId || !backlog || !iterationId) {
            return;
        }

        if (iteration.id == iterationId && team.id == teamId && team.backlog == backlog) {
            const header = document.querySelector<HTMLElement>(getHeaderClass(teamId, backlog, iterationId));
            const addNewItemButton = header?.querySelector<HTMLElement>(".add-new-item-iteration-button");
            addNewItemButton?.click();
        }
    };

    React.useEffect(() => {
        hotKeyEventService.subscribe(newWorkItemCallback, IHotKeyEventType.NewWorkItem);
        return () => hotKeyEventService.unsubscribe(newWorkItemCallback, IHotKeyEventType.NewWorkItem);
    }, []);

    const iterationLayout = teamLayout.iterations.find(i => i.iteration.id === iteration.id);

    return (
        <div
            className={css("focusable-board-item iteration-header", className)}
            data-iteration={iteration.id}
            data-team={team.id}
            data-backlog={team.backlog}
            style={{
                gridColumnStart: startColumn,
                gridColumnEnd: endColumn,
                gridRow: row
            }}
            tabIndex={0}
        >
            <Link
                className="flex-row text-ellipsis"
                href={getSprintHubUrl(pageContext, teamLayout.team.projectId, teamLayout.team.name, iteration.id)}
                target="_blank"
                tooltipProps={{ text: iteration.name }}
            >
                <span className="iteration-name font-weight-semibold text-ellipsis">{iteration.name}</span>
            </Link>
            <div className="second-row flex-row flex-center">
                <Tooltip overflowOnly>
                    <div className="iteration-dates text-ellipsis">
                        {localeDateFormat(iteration.startDate, "m", dateFormatter)} - {localeDateFormat(iteration.endDate, "m", dateFormatter)}
                    </div>
                </Tooltip>
                <Button
                    className={css("flex-noshrink", "add-new-item-iteration-button")}
                    iconProps={addButtonIconProps}
                    onClick={onAddClickCb}
                    tooltipProps={{ text: Resources.AddItemButtonAriaLabel }}
                />
            </div>
            <CollapsedWorkItems
                expanded={teamLayout.expanded}
                keyPrefix={`${teamLayout.team.name}-${teamLayout.team.backlogName}-${iteration.name}`}
                workItemCounts={iterationLayout?.workItemCounts}
                workItemTypeColorAndIcons={teamLayout.team.workItemTypeColorAndIcons}
            />
            {showNewItemCallout && (
                <NewItemCallout
                    allowedPositions={["top", "bottom"]}
                    anchor={showNewItemCallout}
                    colorAndIcons={Object.values(teamLayout.team.workItemTypeColorAndIcons)}
                    getLocalStorageKeyForWorkItemType={() => format(deliveryPlansNewItemStorageKey, teamLayout.team.name, teamLayout.team.backlog)}
                    onAddItem={(title, witType, direction: TopBottom) => onAddItem(teamLayout.team, iteration, title, witType, direction)}
                    onDismiss={() => setShowNewItemCallout(undefined)}
                    getBacklogDefaultWorkItemTypeNameFetcher={{
                        service: backlogContentService,
                        context: { teamId: teamLayout.team.id, backlog: teamLayout.team.backlog }
                    }}
                />
            )}
        </div>
    );
}

function CollapsedWorkItems({
    expanded,
    keyPrefix,
    suppressTooltips,
    workItemCounts,
    workItemTypeColorAndIcons
}: {
    expanded: IObservableValue<boolean>;
    keyPrefix: string;
    suppressTooltips?: boolean;
    workItemCounts?: { [witType: string]: number };
    workItemTypeColorAndIcons: { [type: string]: WorkItemTypeColorAndIcon };
}): JSX.Element {
    return (
        <ConditionalChildren inverse={true} renderChildren={expanded}>
            <div className="flex-row flex-wrap text-ellipsis">
                {Object.values(workItemTypeColorAndIcons).map(colorAndIcon => {
                    const count = workItemCounts ? workItemCounts[colorAndIcon.workItemTypeName] : 0;
                    return count && count > 0 ? (
                        <div
                            key={`${keyPrefix}-${colorAndIcon.workItemTypeName}-count`}
                            className="flex-row margin-top-8 padding-right-8 text-ellipsis"
                        >
                            <WorkItemTypeIcon
                                className="margin-right-8"
                                size={TypeIconSize.large}
                                data={colorAndIcon}
                                suppressTooltip={suppressTooltips}
                            />
                            <span className="font-weight-semibold margin-right-4">{count}</span>
                            <Tooltip overflowOnly>
                                <span className="text-ellipsis">{colorAndIcon.workItemTypeName}</span>
                            </Tooltip>
                        </div>
                    ) : null;
                })}
            </div>
        </ConditionalChildren>
    );
}

function IterationBackground({
    className,
    startColumn,
    endColumn,
    startRow,
    endRow,
    iteration,
    team
}: {
    className?: string;
    startColumn: string | number;
    endColumn: string | number;
    startRow: number;
    endRow: number;
    iteration: IIteration;
    team: ITeam;
}) {
    return (
        <div
            className={css("iteration-background", className)}
            data-iteration={iteration.path}
            data-team={team.id}
            data-backlog={team.backlog}
            style={{
                gridRowStart: startRow,
                gridRowEnd: endRow,
                gridColumnStart: startColumn,
                gridColumnEnd: endColumn
            }}
        />
    );
}

function CollapseTeamsButton({ expanded, expandedCallback }: { expanded: IObservableValue<boolean>; expandedCallback: (expanded: boolean) => void }) {
    return (
        <Observer expanded={expanded}>
            {(props: { expanded: boolean }) => {
                const ariaLabel = props.expanded ? Resources.CollapseAllTeamRows : Resources.ExpandAllTeamRows;
                return (
                    <Button
                        ariaLabel={ariaLabel}
                        className="button-left collapse-teams-button"
                        iconProps={{ iconName: props.expanded ? "ChevronDown" : "ChevronRight", size: IconSize.small }}
                        onClick={() => {
                            expanded.value = !expanded.value;
                            expandedCallback(expanded.value);
                        }}
                        style={{ gridRow: monthHeaderRow, gridColumn: 1 }}
                        text={Resources.CollapseOrExpandTeamsButtonLabel}
                        tooltipProps={{ text: ariaLabel }}
                    />
                );
            }}
        </Observer>
    );
}

/** renders the team row header */
function TeamHeader({
    className,
    endRow,
    expanded,
    expandedCallback,
    onLoadMoreWorkItems,
    pageContext,
    startRow,
    teamLayout
}: {
    className?: string;
    endRow: number;
    expanded: IObservableValue<boolean>;
    expandedCallback: (expanded: boolean) => void;
    onLoadMoreWorkItems: (project: string, workItemIds: number[]) => Promise<void>;
    pageContext: IVssPageContext;
    startRow: number;
    teamLayout: TeamLayout;
}) {
    const team = teamLayout.team;
    const [loading, setLoading] = React.useState(false);
    return (
        <div
            className={css("focusable-board-item team-header flex-row padding-8 rhythm-horizontal-8", className)}
            style={{
                gridRowStart: startRow,
                gridRowEnd: endRow
            }}
            tabIndex={0}
        >
            <div>
                <Observer expanded={expanded}>
                    {(props: { expanded: boolean }) => (
                        <Button
                            ariaLabel={props.expanded ? Resources.CollapseTeamRow : Resources.ExpandTeamRow}
                            iconProps={{ iconName: props.expanded ? "ChevronDown" : "ChevronRight", size: IconSize.small }}
                            onClick={() => {
                                expanded.value = !expanded.value;
                                expandedCallback(expanded.value);
                            }}
                            subtle
                        />
                    )}
                </Observer>
            </div>
            <div className="team-header-info flex-column">
                <Tooltip overflowOnly>
                    <span className="font-weight-semibold text-ellipsis">{team.name}</span>
                </Tooltip>
                <Link
                    className="team-header-info-margin-top"
                    href={getBacklogUrl(pageContext, team.projectId, team.name, team.backlogName)}
                    target="_blank"
                >
                    {team.backlogName}
                </Link>
                <Tooltip showOnFocus text={Resources.CollapsedWorkItemsNoIterationTooltip}>
                    <div tabIndex={0}>
                        <CollapsedWorkItems
                            expanded={teamLayout.expanded}
                            keyPrefix={`${teamLayout.team.name}-${teamLayout.team.backlogName}`}
                            suppressTooltips={true}
                            workItemCounts={teamLayout.workItemCounts}
                            workItemTypeColorAndIcons={teamLayout.team.workItemTypeColorAndIcons}
                        />
                    </div>
                </Tooltip>
                {teamLayout.partialItems.length > 0 && (
                    <Button
                        className="team-header-info-margin-top"
                        onClick={async () => {
                            setLoading(true);
                            await onLoadMoreWorkItems(teamLayout.team.projectId, teamLayout.partialItems);
                            setLoading(false);
                        }}
                        text={Resources.LoadMoreItemsButtonText}
                    />
                )}
                {loading && <Spinner className="team-header-info-margin-top" size={SpinnerSize.large} />}
            </div>
        </div>
    );
}

interface ITeamBackgroundProps {
    className?: string;
    teamIndex: number;
    startRow: number;
    endColumn: number;
    endRow: number;
    team: ITeam;
}

/** renders the team row background */
const TeamBackground = React.forwardRef<HTMLDivElement, ITeamBackgroundProps>(({ className, teamIndex, startRow, endColumn, endRow, team }, ref) => {
    return (
        <div
            className={css("team-background", className)}
            data-backlog={team.backlog}
            data-row-index={teamIndex}
            data-team={team.id}
            ref={ref}
            style={{
                gridRowStart: startRow,
                gridRowEnd: endRow,
                gridColumn: `1 / ${endColumn}`
            }}
        />
    );
});

const TimelineMarkersMemo = React.memo(TimelineMarkers);

function TimelineMarkers({ markers, scale, maxRow }: { markers: BoardMarker[]; scale: number; maxRow: number }): JSX.Element {
    // process the markers from right to left as they would appear on the board
    // simplest case of placing two markers:
    // a marker label on the right will get the lowest available row if there is overlap
    // a marker label on the left that overlaps will be "stacked" and placed the next row up
    const combinedMarkers = markers.sort((a, b) => b.date.getTime() - a.date.getTime());

    let currentRow = monthHeaderRow - 1;
    const markerRows: number[] = [];
    const markerElements = combinedMarkers.map((marker, index) => {
        if (!marker.className && !marker.color) {
            return null;
        }

        const dateString = getDateString(marker.date);
        const gridColumnStart = `c${dateString}-start`;

        // determine if this marker overlaps with any previous markers
        if (index > 0) {
            let overlap = false;
            for (let i = index - 1; i >= 0; i--) {
                const previousMarker = combinedMarkers[i];
                const previousMarkerRow = markerRows[i];
                if (previousMarkerRow <= currentRow && doMarkersOverlap(marker, previousMarker, scale)) {
                    // decrement the row unless we are the maximum stack size of 5
                    // at this point we just let them overlap at the top
                    currentRow = firstMarkerRow === previousMarkerRow ? previousMarkerRow : previousMarkerRow - 1;
                    overlap = true;
                }
            }

            if (!overlap) {
                // if we don't have overlap, reset current row
                currentRow = monthHeaderRow - 1;
            }
        }

        markerRows.push(currentRow);

        return (
            <TimelineMarker
                key={`marker-${marker.label}-${index}`}
                gridColumnStart={gridColumnStart}
                labelRow={currentRow}
                marker={marker}
                maxRow={maxRow}
            />
        );
    });

    // reverse the list so that the tab ordering makes sense
    return <>{...markerElements.reverse()}</>;
}

function TimelineMarker({
    gridColumnStart,
    labelRow,
    marker,
    maxRow
}: {
    gridColumnStart: string;
    labelRow: number;
    marker: BoardMarker;
    maxRow: number;
}): JSX.Element {
    const { className, color, date, label } = marker;
    const parsedColor = parseColor(color);
    const colorStyle = parsedColor && getColorString(parsedColor);
    const activated = React.useRef(new ObservableValue(false));
    const adjustedTime = className === BoardConstants.todayMarker.className ? getDateInUserTimeZone(date) : date;
    return (
        <>
            <div className="timeline-marker-label body-s" style={{ gridColumnStart: gridColumnStart, gridRowStart: labelRow }}>
                <Tooltip showOnFocus={true} text={localeDateFormat(adjustedTime, "d")}>
                    <Pill
                        className={css("inline-flex-row", className)}
                        color={parsedColor}
                        onClick={() => (activated.current.value = !activated.current.value)}
                        size={PillSize.compact}
                        variant={parsedColor ? PillVariant.colored : PillVariant.standard}
                    >
                        {label}
                    </Pill>
                </Tooltip>
            </div>
            <Observer activated={activated.current}>
                {(props: { activated: boolean }) => (
                    <div
                        className={css("timeline-marker", props.activated && "raised", className)}
                        style={{ borderLeftColor: colorStyle, gridColumnStart, gridRowStart: labelRow, gridRowEnd: maxRow }}
                    />
                )}
            </Observer>
        </>
    );
}

function filterMarkers(markers: Marker[], renderedSpan: ISpan) {
    return [...markers, BoardConstants.todayMarker].filter(marker => isDateInSpan(marker.date, renderedSpan));
}

function getDateFromString(s: string): Date {
    return new Date(s);
}

const getScrollingButtonsMemo = memoizeFunction(getScrollingButtons);

/**
 * Get left and right scrolling buttons
 * These buttons get wrapped in a div for compatibility with StickyZone
 */
function getScrollingButtons(onScroll: (direction: "right" | "left", duration: number) => void, endColumn: number) {
    return [
        <div className="scrolling-button button-left" key="left-scrolling-button" style={{ gridRow: monthHeaderRow }}>
            <ScrollingButton alignment="left" onScroll={onScroll} />
        </div>,
        <div
            className="scrolling-button button-right"
            key="right-scrolling-button"
            style={{
                gridRow: monthHeaderRow,
                gridColumn: endColumn
            }}
        >
            <ScrollingButton alignment="right" onScroll={onScroll} />
        </div>
    ];
}

function ScrollingButton({
    alignment,
    onScroll
}: {
    alignment: "right" | "left";
    onScroll: (direction: "right" | "left", duration: number) => void;
}): JSX.Element {
    const isRightAligned = alignment === "right";
    return (
        <Button
            iconProps={{ iconName: isRightAligned ? "ChevronRight" : "ChevronLeft", size: IconSize.small }}
            onClick={() => onScroll(alignment, 300)}
            primary
            tooltipProps={{
                text: format(Resources.ShiftCalendarButtonAriaLabel, isRightAligned ? Resources.DirectionRight : Resources.DirectionLeft)
            }}
        />
    );
}

function getDateString(d: Date): string;
function getDateString(year: number, month: number, day: number): string;
function getDateString(year: number | Date, month?: number, day?: number): string {
    if (year instanceof Date) {
        day = year.getDate();
        month = year.getMonth() + 1;
        year = year.getFullYear();
    }
    return `${year}-${month}-${day}`;
}

function doMarkersOverlap(left: Marker, right: Marker, scale: number): boolean {
    const previousMarkerDate = right.date;
    const cellsBetween = daysInSpan(left.date, previousMarkerDate);

    // check how long the label is compared to the distance between markers
    const expectedLabelCellWidth = (left.label.length + BoardConstants.pillPadding) / (BoardConstants.characterToCellRatio * scale);
    return expectedLabelCellWidth >= cellsBetween;
}

function animateScroll(action: (progress: number) => void, duration: number) {
    const animate = (duration: number, resolve: (value?: unknown) => void) => {
        let start: number | undefined = undefined;
        const step = (time: number) => {
            if (start === undefined) {
                start = time;
            }

            // Start at 0 and incrementally increase to 1 - linear animation
            const progress = (time - start) / duration;
            if (progress > 1) {
                action(1);
                resolve();
                return;
            } else {
                requestAnimationFrame(step);
                action(progress);
            }
        };

        requestAnimationFrame(step);
    };

    return new Promise(resolve => {
        animate(duration, resolve);
    });
}

/**
 * Iterate over siblings until finding a match for className
 * @param element
 * @param classNameToFind
 * @param direction
 */
function getNextSibling(element: HTMLElement, classNameToFind: string, direction: "right" | "left"): HTMLElement | undefined {
    if (element.classList.contains(classNameToFind)) {
        // We have a match
        return element;
    }

    const nextSibling = (direction === "right" ? element.nextElementSibling : element.previousElementSibling) as HTMLElement | undefined;
    if (!nextSibling) {
        // No matches
        return undefined;
    }

    // Keep looking
    return getNextSibling(nextSibling, classNameToFind, direction);
}
